import { zodResolver } from '@hookform/resolvers/zod';
import {
  createKbArticleFormSchema,
  KbVisibility,
  parseTagInput,
  parseTicketNumberInput,
  PermissionCode,
  type CreateKbArticleFormValues,
  type CreateKbArticleRequest,
  type UpdateKbArticleRequest,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useVoiceCopy } from '@/platform/voice/use-voice';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import {
  useCreateKbArticle,
  useKbArticle,
  useKbLookups,
  useUpdateKbArticle,
} from '@/products/helpdesk/features/kb/use-kb';
import { RichTextEditor } from '@/products/helpdesk/features/tickets/rich-text-editor';
import { ApiRequestError } from '@/lib/api';

const NONE = 'none';

export function KbArticleEditorPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.KB_WRITE);
  const canPublish = hasPermission(me.data, PermissionCode.KB_PUBLISH);
  const copy = useVoiceCopy();
  const lookups = useKbLookups();
  const existing = useKbArticle(slug);
  const createArticle = useCreateKbArticle();
  const updateArticle = useUpdateKbArticle(slug ?? '');
  const ticketFromQuery = params.get('ticket') ?? '';

  const form = useForm<CreateKbArticleFormValues>({
    resolver: zodResolver(createKbArticleFormSchema),
    defaultValues: {
      title: '',
      body: '',
      summary: '',
      categoryId: NONE,
      visibility: KbVisibility.INTERNAL,
      tags: '',
      ticketNumbers: ticketFromQuery,
    },
  });

  useEffect(() => {
    if (!existing.data) {
      return;
    }
    form.reset({
      title: existing.data.title,
      body: existing.data.body,
      summary: existing.data.summary ?? '',
      categoryId: existing.data.category?.id ?? NONE,
      visibility: existing.data.visibility,
      tags: existing.data.tags.map((tag) => tag.name).join(', '),
      ticketNumbers: existing.data.tickets.map((ticket) => String(ticket.number)).join(', '),
    });
  }, [existing.data, form]);

  if (!canWrite) {
    return (
      <section className="mx-auto max-w-md space-y-3 text-center">
        <h1 className="text-2xl font-bold">Kein Zugriff</h1>
        <p className="text-muted-foreground">{copy('helpdesk.kb.write.denied')}</p>
      </section>
    );
  }

  async function onSubmit(values: CreateKbArticleFormValues) {
    const tags = parseTagInput(values.tags);
    const ticketNumbers = parseTicketNumberInput(values.ticketNumbers);
    try {
      if (slug && existing.data) {
        const body: UpdateKbArticleRequest = {
          title: values.title,
          body: values.body,
          summary: values.summary?.trim() ? values.summary.trim() : null,
          categoryId: values.categoryId && values.categoryId !== NONE ? values.categoryId : null,
          visibility: values.visibility,
          tags,
        };
        const updated = await updateArticle.mutateAsync(body);
        toast.success('Artikel gespeichert (neue Version, falls sich der Inhalt geändert hat).');
        void navigate(`/helpdesk/wissen/${updated.slug}`);
        return;
      }
      const body: CreateKbArticleRequest = {
        title: values.title,
        body: values.body,
        summary: values.summary?.trim() ? values.summary.trim() : undefined,
        categoryId: values.categoryId && values.categoryId !== NONE ? values.categoryId : null,
        visibility: values.visibility,
        status: canPublish ? 'PUBLISHED' : 'DRAFT',
        tags,
        ticketNumbers,
      };
      const created = await createArticle.mutateAsync(body);
      toast.success(canPublish ? 'Artikel veröffentlicht.' : 'Entwurf gespeichert.');
      void navigate(`/helpdesk/wissen/${created.slug}`);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  const pending = createArticle.isPending || updateArticle.isPending;

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to={slug ? `/helpdesk/wissen/${slug}` : '/helpdesk/wissen'}>Zurück</Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          {slug ? 'Artikel bearbeiten' : 'Neuer Artikel'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Inhalt als Rich Text. Änderungen an Titel, Kurztext oder Inhalt erzeugen eine neue
          Version.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Inhalt</CardTitle>
          <CardDescription>
            Öffentliche Artikel sind später im Self-Service sichtbar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}>
            <div className="space-y-1.5">
              <Label htmlFor="kb-title">Titel</Label>
              <Input id="kb-title" {...form.register('title')} />
              {form.formState.errors.title && (
                <p className="text-sm text-destructive">{form.formState.errors.title.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kb-summary">Kurztext</Label>
              <Input id="kb-summary" {...form.register('summary')} />
            </div>
            <div className="space-y-1.5">
              <Label>Inhalt</Label>
              <Controller
                control={form.control}
                name="body"
                render={({ field, fieldState }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    invalid={Boolean(fieldState.error)}
                    placeholder="Lösungsschritte, Voraussetzungen, Hinweise …"
                  />
                )}
              />
              {form.formState.errors.body && (
                <p className="text-sm text-destructive">{form.formState.errors.body.message}</p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Kategorie</Label>
                <Controller
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <Select value={field.value ?? NONE} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Keine" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Keine Kategorie</SelectItem>
                        {(lookups.data?.categories ?? []).map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Sichtbarkeit</Label>
                <Controller
                  control={form.control}
                  name="visibility"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={KbVisibility.INTERNAL}>Intern</SelectItem>
                        <SelectItem value={KbVisibility.PUBLIC}>
                          Öffentlich (Self-Service)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kb-tags">Tags (kommagetrennt)</Label>
              <Input id="kb-tags" {...form.register('tags')} placeholder="VPN, Outlook" />
            </div>
            {!slug && (
              <div className="space-y-1.5">
                <Label htmlFor="kb-tickets">Ursprungstickets (Nummern, kommagetrennt)</Label>
                <Input id="kb-tickets" {...form.register('ticketNumbers')} placeholder="12, 15" />
              </div>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={pending}>
                {pending && <Loader2Icon className="animate-spin" aria-hidden />}
                {slug ? 'Speichern' : canPublish ? 'Veröffentlichen' : 'Entwurf speichern'}
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/helpdesk/wissen">Abbrechen</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
